import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import {
  assignOrganization,
  createOrganization,
} from '../../store/actions/OnboardingActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';

import TagInput from '../shared/TailwindComponents/TagInput';
import { getAuth0Email } from '../../auth0-integration/utils/auth0EmailUtils';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const OrganizationCheckStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: auth0User } = useAuth0();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const domainInitializedRef = useRef(false);
  const [organizationData, setOrganizationData] = useState({
    name: '',
    type: 'law_firm',
    email_domains: [] as string[],
    org_details: {
      regions: [] as string[],
      description: '',
    },
    active_license_count: 1,
  });
  const [showOrgDetails, setShowOrgDetails] = useState(false);

  const realEmail = getAuth0Email(auth0User);
  const domainFromEmail = realEmail?.split('@')[1] || '';

  useEffect(() => {
    if (domainFromEmail && !domainInitializedRef.current) {
      setOrganizationData((prev) => ({
        ...prev,
        email_domains: [domainFromEmail],
      }));
      domainInitializedRef.current = true;
    }
  }, [domainFromEmail]);

  const handleSelectOrganization = async (orgUuid: string) => {
    try {
      await dispatch(assignOrganization(orgUuid) as any);
    } catch (error) {
      console.error('Error selecting organization:', error);
    }
  };


  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationData.name || !organizationData.type || organizationData.email_domains.length === 0) {
      return;
    }
    try {
      await dispatch(
        createOrganization({
          name: organizationData.name,
          type: organizationData.type,
          email_domains: organizationData.email_domains,
          org_details: organizationData.org_details,
          active_license_count: organizationData.active_license_count,
        }) as any
      );
    } catch (error) {
      console.error('Error creating organization:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setOrganizationData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const hasMatchingOrganizations = onboarding.matchingOrganizations && onboarding.matchingOrganizations.length > 0;
  const hasExistingOrganization = onboarding.selectedOrganization?.id || onboarding.user?.organization_id;

  // If user already belongs to an organization, show confirmation (read-only)
  if (hasExistingOrganization) {
    const orgName = onboarding.selectedOrganization?.name || 'Your Organization';
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('Confirm Your Organization')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t("You're assigned to this organization. Please confirm and continue.")}
        </p>

        <div className="rounded-lg border-2 border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/10 p-5 mb-4">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-6 h-6 text-success-600 dark:text-success-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {orgName}
              </h3>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              dispatch({ type: 'ONBOARDING_SET_STEP', payload: 'user' });
            }}
          >
            {t('Confirm & Continue')}
          </Button>
        </div>
      </div>
    );
  }

  // If we have matching organizations from domain check, show options to join
  if (hasMatchingOrganizations) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{t('Join your organization')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          We found {onboarding.matchingOrganizations.length} organization{onboarding.matchingOrganizations.length !== 1 ? 's' : ''} matching your email domain.
        </p>

        <div className="space-y-3">
          {onboarding.matchingOrganizations.map((org) => (
            <div
              key={org.id}
              className="flex items-center justify-between p-4 border-2 border-primary-200 dark:border-primary-700 rounded-lg bg-primary-50 dark:bg-primary-900/10 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
            >
              <div className="flex flex-col gap-1 flex-1">
                <p className="font-semibold text-gray-900 dark:text-white text-sm">{org.name}</p>
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleSelectOrganization(org.uuid)}
                disabled={onboarding.isLoading}
                isLoading={onboarding.isLoading}
              >
                {t('Join Organization')}
              </Button>
            </div>
          ))}
        </div>

        {/* <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Don't see your organization? Create a new one below.
          </p>
        </div> */}
      </div>
    );
  }

  // No matching organization - show creation form
  return (
    <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6 max-h-[calc(100vh-100px)] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{t('Create your organization')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t("Create a new organization to get started. You'll be set as the organization admin.")}
      </p>

      <form onSubmit={handleCreateOrganization} className="space-y-4">
        {/* Basic Info */}
        <div>
          <Input
            type="text"
            label={t('Organization Name')}
            placeholder={t('e.g., Acme Law Firm')}
            value={organizationData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />
        </div>



        {/* Email Domains - Read-only, locked to signup domain */}
        <div>
          <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
            {t('Email Domain')}
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('Your organization is locked to the email domain you signed up with.')}
          </p>
          {organizationData.email_domains.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-success-50 dark:bg-success-900/10 rounded border border-success-200 dark:border-success-700">
              <CheckCircleIcon className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {organizationData.email_domains[0]}
              </span>
            </div>
          )}
        </div>

        {/* Optional Details Toggle */}
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setShowOrgDetails(!showOrgDetails)}
            className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
          >
            {showOrgDetails ? '▼' : '▶'} {t('Additional Details (Optional)')}
          </button>
        </div>

        {/* Org Details - Regions, Description */}
        {showOrgDetails && (
          <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t('Add organizational details. You can update these later.')}
            </p>

            <div>
              <TagInput
                id="regions-input"
                label={t('Regions')}
                placeholder={t("Write the regions, cities or countries you operate in, followed by ',' or enter")}
                tags={organizationData.org_details.regions}
                onChange={(regions) =>
                  setOrganizationData((prev) => ({
                    ...prev,
                    org_details: {
                      ...prev.org_details,
                      regions,
                    },
                  }))
                }
              />
            </div>

            <div>
              <Input
                type="text"
                label={t('Description')}
                placeholder={t('e.g., Law firm specializing in corporate law')}
                value={organizationData.org_details.description}
                onChange={(e) =>
                  setOrganizationData((prev) => ({
                    ...prev,
                    org_details: {
                      ...prev.org_details,
                      description: e.target.value,
                    },
                  }))
                }
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={!organizationData.name || organizationData.email_domains.length === 0 || onboarding.isLoading}
            isLoading={onboarding.isLoading}
          >
            {t('Create Organization')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationCheckStep;
